<template>
  <main class="cover-auth">
    <div class="widget-auth">
      <div class="flex-center">
        <img 
          :src="userPhoto || '/public/assets/users/300w/user.jpg'" 
          class="rounded-100 overflow-hidden size-4xl item active" 
          alt="Photo de profil"
        />
      </div>

      <h2 class="font-semibold my-6 text-center">{{ userName || 'Connexion' }}</h2>
      
      <form class="form" @submit.prevent="handleSubmit">              
        <div class="form-field-floating xl reactive active rounded-50 text-center mt-6">
          <input 
            type="text" 
            class="form-input text-center" 
            name="identifiant" 
            id="identifiant" 
            v-model="formData.identifiant"
            placeholder=""
          >
          <label for="identifiant">Email ou numéro de téléphone <b class="color-danger">*</b></label>
        </div>
        
        <h6 class="font-normal text-grey-600 text-center mt-5 mb-3">
          Entrez votre code de sécurité
        </h6>
        
        <div class="flex gap-4 mb-7">
          <input 
            v-for="(digit, index) in securityCode" 
            :key="index"
            type="password" 
            class="form-control-code" 
            :id="`code-${index}`"
            v-model="securityCode[index]"
            maxlength="1"
            @input="handleInput(index, $event)"
            @keydown="handleKeydown(index, $event)"
            @paste="handlePaste"
            placeholder=""
            inputmode="numeric"
            pattern="[0-9]"
          >
        </div>
        
        <button 
          type="submit" 
          class="btn lg w-100 flex-center mt-5 mb-7"
          :disabled="!isFormValid || isSubmitting"
        >
          <img src="/public/icons/icon-menu-securite.svg" class="btn-icon" alt="" />
          <span>{{ isSubmitting ? 'Connexion...' : 'Connexion' }}</span>
        </button>
        
        <div class="flex-center gap-6">
          <router-link to="/forget-password">
            <h5 class="text-primary-color text-center">Besoin d'aide ?</h5>
          </router-link>
          <router-link to="/signup">
            <h5 class="text-primary-color text-center">Créer un compte !</h5>
          </router-link>
        </div>
      </form>
    </div>

    <p class="text-sm text-grey-600 text-center position-absolute bottom mb-6">
      © Tous droits réservés COOP FINANCE 2026 &nbsp; 
      <router-link to="/aide">Besoin d'aide ?</router-link>
    </p>
  </main>
</template>

<script lang="ts">
import { defineComponent, reactive, computed, nextTick, ref } from 'vue'
import { useRouter } from 'vue-router'
import authService from '@/services/authService'
import { showAlert } from '@/utils/swal'

interface FormData {
  identifiant: string
}

export default defineComponent({
  name: 'SignIn',
  props: {
    userName: {
      type: String,
      default: ''
    },
    userPhoto: {
      type: String,
      default: ''
    }
  },
  setup() {
    const router = useRouter()
    const formData = reactive<FormData>({
      identifiant: ''
    })
    const securityCode = reactive<string[]>(['', '', '', ''])
    const isSubmitting = ref(false)

    const isCodeComplete = computed(() => {
      return securityCode.every(digit => digit !== '' && /^\d$/.test(digit))
    })

    const isFormValid = computed(() => {
      return formData.identifiant.trim() !== '' && isCodeComplete.value
    })

    const focusInput = (index: number) => {
      nextTick(() => {
        const input = document.getElementById(`code-${index}`) as HTMLInputElement
        if (input) {
          input.focus()
        }
      })
    }

    const handleInput = (index: number, event: Event) => {
      const target = event.target as HTMLInputElement
      const value = target.value

      // Ne garder que les chiffres
      if (!/^\d*$/.test(value)) {
        securityCode[index] = ''
        return
      }

      // Si on entre un chiffre, passer au champ suivant
      if (value.length === 1 && index < securityCode.length - 1) {
        focusInput(index + 1)
      }
    }

    const handleKeydown = (index: number, event: KeyboardEvent) => {
      // Gérer la touche Backspace
      if (event.key === 'Backspace' && securityCode[index] === '' && index > 0) {
        event.preventDefault()
        focusInput(index - 1)
      }

      // Gérer les flèches gauche/droite
      if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault()
        focusInput(index - 1)
      }

      if (event.key === 'ArrowRight' && index < securityCode.length - 1) {
        event.preventDefault()
        focusInput(index + 1)
      }
    }

    const handlePaste = (event: ClipboardEvent) => {
      event.preventDefault()
      const pastedData = event.clipboardData?.getData('text')
      
      if (pastedData && /^\d{4}$/.test(pastedData)) {
        for (let i = 0; i < securityCode.length; i++) {
          securityCode[i] = pastedData[i]
        }
        focusInput(securityCode.length - 1)
      }
    }

    const validateEmail = (email: string): boolean => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    }

    const validatePhone = (phone: string): boolean => {
      const phoneRegex = /^[\d\s+\-()]+$/
      return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 8
    }

    const handleSubmit = async () => {
      if (!formData.identifiant.trim()) {
        await showAlert.warning('Veuillez saisir votre email ou numéro de téléphone')
        return
      }

      const isEmail = formData.identifiant.includes('@')
      const isValid = isEmail 
        ? validateEmail(formData.identifiant) 
        : validatePhone(formData.identifiant)

      if (!isValid) {
        await showAlert.warning('Veuillez saisir un email ou un numéro de téléphone valide')
        return
      }

      if (!isCodeComplete.value) {
        await showAlert.warning('Veuillez saisir votre code de sécurité à 4 chiffres')
        return
      }

      isSubmitting.value = true

      try {
        const code = securityCode.join('')
        
        // Stocker l'identifiant pour la page OTP
        sessionStorage.setItem('loginUsername', formData.identifiant)
        
        // Vérifier les identifiants et demander l'envoi de l'OTP
        const response = await authService.requestLogin({
          identifiant: formData.identifiant,
          codeSecurite: code
        })
        
        if (response.status === 0) {
          await showAlert.success(response.message || 'Un code de vérification a été envoyé à votre adresse email')
          // Rediriger vers la page OTP
          const redirect = router.currentRoute.value.query.redirect as string | undefined
          const query = redirect ? { redirect } : undefined
          router.push({ path: '/auth/sign-in-otp', query })
        } else {
          await showAlert.error(response.message || 'Identifiants incorrects. Veuillez réessayer.')
        }
      } catch (error) {
        console.error('Erreur lors de la connexion:', error)
        await showAlert.error('Une erreur est survenue. Veuillez réessayer.')
      } finally {
        isSubmitting.value = false
      }
    }

    return {
      formData,
      securityCode,
      isSubmitting,
      isFormValid,
      handleInput,
      handleKeydown,
      handlePaste,
      handleSubmit
    }
  }
})
</script>

<style scoped>
.flex-center {
  display: flex;
  justify-content: center;
  align-items: center;
}

.rounded-100 {
  border-radius: 100%;
}

.overflow-hidden {
  overflow: hidden;
}

.size-4xl {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border: 4px solid var(--primary-color, #007bff);
}

.item.active {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(0, 123, 255, 0.4);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(0, 123, 255, 0);
  }
}

.rounded-50 {
  border-radius: 50px;
}

.form-control-code {
  width: 60px;
  height: 60px;
  text-align: center;
  font-size: 24px;
  font-weight: bold;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.form-control-code:focus {
  outline: none;
  border-color: var(--primary-color, #007bff);
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.form-control-code:not(:placeholder-shown) {
  border-color: var(--primary-color, #007bff);
  background-color: #f8f9fa;
}

.flex {
  display: flex;
  justify-content: center;
}

.gap-4 {
  gap: 1rem;
}

.gap-6 {
  gap: 1.5rem;
}

.position-absolute {
  position: absolute;
}

.bottom {
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .form-control-code {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
  
  .size-4xl {
    width: 100px;
    height: 100px;
  }
}
</style>
